import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoaderBar } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { AutoTicketService } from 'utility/services/auto-ticket'
import { ActionPlanRuleService, InspectionService } from 'utility'
import { getApiErrorMessage } from 'page-components/auto-ticket-rules/utils'
import {
    TICKET_STATUS,
    TICKET_PRIORITY,
    normalizeTemplateTicketList,
    normalizeAuditTicketsByAudit,
    normalizeDigestResponse,
    resolveTemplateNumericIdFromAudit,
    enrichAuditTickets,
    computeTicketStatusCounts,
    ticketMatchesAuditListFilters,
    getPriorityDisplayLabel,
    getStatusDisplayLabel,
    getTicketStatusWhenLabel,
    formatTicketDateTime,
} from './utils'
import {
    ListPageWrap,
    ListPageInner,
    PublicListWrap,
    BackRow,
    ListTitle,
    ListSubtitle,
    AuditHeaderCard,
    AuditDl,
    CountRow,
    StatBadge,
    FiltersCard,
    FilterField,
    FilterActionsGroup,
    FilterBtn,
    TicketListCard,
    TicketTableScroll,
    TicketTable,
    TicketMobileList,
    TicketMobileCard,
    MobileCardTop,
    MobileCardId,
    MobileCardArrow,
    MobileCardMeta,
    MobileCardFooter,
    ColId,
    PriorityPill,
    QuestionCell,
    StatusBadge,
    WhenText,
    AssigneeCell,
    RowAction,
    Pager,
    EmptyCard,
    ErrorText,
} from './audit-tickets-list-styles'

const ALL = ''

const DEFAULT_FILTERS = {
    statusId: ALL,
    priorityId: ALL,
    assigneeKey: ALL,
    search: '',
}

const STATUS_FILTER_OPTIONS = [
    { value: ALL, label: 'All' },
    { value: '1', label: 'Open' },
    { value: '2', label: 'Acknowledged' },
    { value: '3', label: 'In Progress' },
    { value: '4', label: 'Resolved' },
    { value: '5', label: 'Closed' },
    { value: '7', label: 'Escalated' },
    { value: '8', label: 'Pending approval' },
    { value: '9', label: 'Cancelled' },
]

const PRIORITY_FILTER_OPTIONS = [
    { value: ALL, label: 'All' },
    { value: '4', label: 'Urgent' },
    { value: '3', label: 'High' },
    { value: '2', label: 'Medium' },
    { value: '1', label: 'Low' },
]

const getTicketDisplayMeta = ticket => {
    const statusMeta = TICKET_STATUS[ticket.statusId] || TICKET_STATUS[1]
    const priorityMeta = TICKET_PRIORITY[ticket.priorityId] || TICKET_PRIORITY[2]
    const assigneeLabel =
        ticket.assigneeFullName?.trim() ||
        ticket.assigneeEmail?.trim() ||
        '—'
    return { statusMeta, priorityMeta, assigneeLabel }
}

const renderTicketMobileCard = (ticket, openTicket) => {
    const { statusMeta, priorityMeta, assigneeLabel } =
        getTicketDisplayMeta(ticket)

    return (
        <TicketMobileCard
            key={ticket.ticketId}
            type="button"
            onClick={() => openTicket(ticket.ticketId)}
        >
            <MobileCardTop>
                <MobileCardId>#{ticket.ticketId}</MobileCardId>
                <MobileCardArrow aria-hidden>→</MobileCardArrow>
            </MobileCardTop>
            <MobileCardMeta>
                <PriorityPill $pill={priorityMeta.pill}>
                    {getPriorityDisplayLabel(ticket.priorityId)}
                </PriorityPill>
                <StatusBadge $pill={statusMeta.pill}>
                    {getStatusDisplayLabel(ticket.statusId)}
                </StatusBadge>
                <WhenText>{getTicketStatusWhenLabel(ticket)}</WhenText>
            </MobileCardMeta>
            <QuestionCell>
                <div className="q">
                    {ticket.questionText || ticket.title || '—'}
                </div>
                {ticket.answerText && (
                    <span className="a">Answer: {ticket.answerText}</span>
                )}
            </QuestionCell>
            <MobileCardFooter>
                <AssigneeCell>
                    {assigneeLabel}
                    {ticket.assigneeEmail &&
                        ticket.assigneeFullName &&
                        ticket.assigneeEmail.trim().toLowerCase() !==
                            ticket.assigneeFullName.trim().toLowerCase() && (
                            <span className="email">{ticket.assigneeEmail}</span>
                        )}
                </AssigneeCell>
            </MobileCardFooter>
        </TicketMobileCard>
    )
}

const loadAuditTickets = async auditId => {
    try {
        const byAuditRes = await AutoTicketService.getByAudit(auditId)
        const normalized = normalizeAuditTicketsByAudit(byAuditRes)
        const counts =
            normalized.counts.total > 0
                ? normalized.counts
                : computeTicketStatusCounts(normalized.tickets)
        return {
            header: normalized,
            tickets: normalized.tickets,
            counts,
        }
    } catch (err) {
        if (err?.status && err.status !== 404 && err.status !== 501) {
            throw err
        }
    }

    const audit = await InspectionService.getAudit(auditId)
    const templateNumericId = resolveTemplateNumericIdFromAudit(audit)
    if (!templateNumericId) {
        throw new Error('Could not resolve template for this audit.')
    }

    const ticketsRes = await ActionPlanRuleService.getTicketsByTemplate(
        templateNumericId,
    )
    const filtered = enrichAuditTickets(
        normalizeTemplateTicketList(ticketsRes).filter(
            t => Number(t.auditId) === Number(auditId),
        ),
        audit,
    )

    return {
        header: {
            auditId: Number(auditId),
            auditName:
                audit?.AuditName ??
                audit?.TemplateName ??
                audit?.Name ??
                `Audit #${auditId}`,
            templateName: audit?.AuditName ?? audit?.TemplateName ?? '',
            completedByName: audit?.CreatedByName ?? audit?.UpdatedByName ?? '',
            completedByEmail: audit?.CreatedByEmail ?? '',
            completedOn: audit?.CompletedOn ?? audit?.UpdatedOn,
        },
        tickets: filtered,
        counts: computeTicketStatusCounts(filtered),
    }
}

export const AuditTicketsListPage = ({
    auditId,
    digestToken,
    layout = 'secure',
}) => {
    const navigate = useNavigate()
    const isPublic = layout === 'public'
    const PageWrap = isPublic ? PublicListWrap : ListPageWrap
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [header, setHeader] = useState(null)
    const [tickets, setTickets] = useState([])
    const [counts, setCounts] = useState(null)
    const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS)
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const load = useCallback(async () => {
        if (!auditId && !digestToken) {
            setError(
                isPublic
                    ? 'Invalid digest link.'
                    : 'Missing auditId in the link.',
            )
            setLoading(false)
            return
        }
        setLoading(true)
        setError('')
        try {
            if (digestToken) {
                const res = await AutoTicketService.getDigest(digestToken)
                const normalized = normalizeDigestResponse(res)
                setHeader(normalized)
                setTickets(normalized.tickets)
                setCounts(
                    normalized.counts?.total > 0
                        ? normalized.counts
                        : computeTicketStatusCounts(normalized.tickets),
                )
                setPage(1)
                if (normalized.tickets.length === 1) {
                    navigate(
                        `/app/secure/tickets/${normalized.tickets[0].ticketId}`,
                        { replace: true },
                    )
                }
                return
            }

            const result = await loadAuditTickets(auditId)
            setHeader(result.header)
            setTickets(result.tickets)
            setCounts(result.counts)
            setPage(1)
        } catch (err) {
            if (digestToken) {
                const status = err?.status
                if (status === 410) {
                    setError('This digest link has expired.')
                } else if (status === 404) {
                    setError('Digest link not found.')
                } else {
                    setError(getApiErrorMessage(err))
                }
            } else {
                setError(getApiErrorMessage(err))
            }
            setTickets([])
        } finally {
            setLoading(false)
        }
    }, [auditId, digestToken, navigate, isPublic])

    useEffect(() => {
        load()
    }, [load])

    const assigneeOptions = useMemo(() => {
        const map = new Map()
        tickets.forEach(t => {
            const name = (t.assigneeFullName || '').trim()
            const email = (t.assigneeEmail || '').trim()
            const key = `${name}|${email}`
            if (!name && !email) return
            const label = name || email
            map.set(key, label)
        })
        return Array.from(map.entries()).map(([value, label]) => ({
            value,
            label,
        }))
    }, [tickets])

    const filteredTickets = useMemo(
        () =>
            tickets.filter(t =>
                ticketMatchesAuditListFilters(t, {
                    statusId: appliedFilters.statusId || null,
                    priorityId: appliedFilters.priorityId || null,
                    assigneeKey: appliedFilters.assigneeKey || null,
                    search: appliedFilters.search,
                }),
            ),
        [tickets, appliedFilters],
    )

    const totalPages = Math.max(
        1,
        Math.ceil(filteredTickets.length / pageSize) || 1,
    )
    const safePage = Math.min(page, totalPages)
    const pageTickets = useMemo(() => {
        const start = (safePage - 1) * pageSize
        return filteredTickets.slice(start, start + pageSize)
    }, [filteredTickets, safePage, pageSize])

    const pageStart =
        filteredTickets.length === 0 ? 0 : (safePage - 1) * pageSize + 1
    const pageEnd = Math.min(safePage * pageSize, filteredTickets.length)

    const applyFilters = () => {
        setAppliedFilters({ ...draftFilters })
        setPage(1)
    }

    const resetFilters = () => {
        setDraftFilters(DEFAULT_FILTERS)
        setAppliedFilters(DEFAULT_FILTERS)
        setPage(1)
    }

    const openTicket = ticketId => {
        navigate(`/app/secure/tickets/${ticketId}`)
    }

    const displayAuditId = header?.auditId || auditId

    if (loading) {
        return (
            <PageWrap>
                <LoaderBar content="Loading tickets…" />
            </PageWrap>
        )
    }

    const displayCounts = counts || computeTicketStatusCounts(tickets)

    return (
        <PageWrap>
            <ListPageInner>
                {!isPublic && (
                    <BackRow>
                        <button
                            type="button"
                            onClick={() => navigate('/app/templates')}
                        >
                            ← Back to templates
                        </button>
                    </BackRow>
                )}

                <ListTitle>
                    Auto-tickets from Audit #{displayAuditId || '—'}
                </ListTitle>
                <ListSubtitle>
                    {tickets.length === 1
                        ? '1 ticket was automatically created from this audit completion.'
                        : `${tickets.length} tickets were automatically created from this audit completion.`}
                </ListSubtitle>

                {error && <ErrorText>{error}</ErrorText>}

                {header && (
                    <AuditHeaderCard>
                        <AuditDl>
                            <dt>Template</dt>
                            <dd>
                                {header.templateName ||
                                    header.auditName ||
                                    '—'}
                            </dd>
                            <dt>Completed by</dt>
                            <dd>
                                {header.completedByName || '—'}
                                {header.completedByEmail
                                    ? ` · ${header.completedByEmail}`
                                    : ''}
                            </dd>
                            <dt>Completed on</dt>
                            <dd>
                                {formatTicketDateTime(header.completedOn)}
                            </dd>
                        </AuditDl>
                        <CountRow>
                            <StatBadge $dot="#111827">
                                <span className="dot" />
                                <span className="num">{displayCounts.total}</span>
                                <span className="lbl">Total</span>
                            </StatBadge>
                            <StatBadge $dot="#0b63ce">
                                <span className="dot" />
                                <span className="num">{displayCounts.open}</span>
                                <span className="lbl">Open</span>
                            </StatBadge>
                            <StatBadge $dot="#d97706">
                                <span className="dot" />
                                <span className="num">
                                    {displayCounts.inProgress}
                                </span>
                                <span className="lbl">In Progress</span>
                            </StatBadge>
                            <StatBadge $dot="#15803d">
                                <span className="dot" />
                                <span className="num">
                                    {displayCounts.resolved}
                                </span>
                                <span className="lbl">Resolved</span>
                            </StatBadge>
                            <StatBadge $dot="#6b7280">
                                <span className="dot" />
                                <span className="num">{displayCounts.closed}</span>
                                <span className="lbl">Closed</span>
                            </StatBadge>
                        </CountRow>
                    </AuditHeaderCard>
                )}

                {tickets.length > 0 && (
                    <>
                        <FiltersCard>
                            <FilterField>
                                <label htmlFor="ticket-status-filter">Status</label>
                                <select
                                    id="ticket-status-filter"
                                    value={draftFilters.statusId}
                                    onChange={e =>
                                        setDraftFilters(f => ({
                                            ...f,
                                            statusId: e.target.value,
                                        }))
                                    }
                                >
                                    {STATUS_FILTER_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </FilterField>
                            <FilterField>
                                <label htmlFor="ticket-priority-filter">
                                    Priority
                                </label>
                                <select
                                    id="ticket-priority-filter"
                                    value={draftFilters.priorityId}
                                    onChange={e =>
                                        setDraftFilters(f => ({
                                            ...f,
                                            priorityId: e.target.value,
                                        }))
                                    }
                                >
                                    {PRIORITY_FILTER_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </FilterField>
                            <FilterField>
                                <label htmlFor="ticket-assignee-filter">
                                    Assignee
                                </label>
                                <select
                                    id="ticket-assignee-filter"
                                    value={draftFilters.assigneeKey}
                                    onChange={e =>
                                        setDraftFilters(f => ({
                                            ...f,
                                            assigneeKey: e.target.value,
                                        }))
                                    }
                                >
                                    <option value={ALL}>All</option>
                                    {assigneeOptions.map(o => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </FilterField>
                            <FilterField className="grow">
                                <input
                                    type="text"
                                    placeholder="Search question or answer text…"
                                    value={draftFilters.search}
                                    onChange={e =>
                                        setDraftFilters(f => ({
                                            ...f,
                                            search: e.target.value,
                                        }))
                                    }
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') applyFilters()
                                    }}
                                />
                            </FilterField>
                            <FilterActionsGroup>
                                <FilterBtn type="button" onClick={applyFilters}>
                                    Apply
                                </FilterBtn>
                                <FilterBtn
                                    type="button"
                                    $ghost
                                    onClick={resetFilters}
                                >
                                    Reset
                                </FilterBtn>
                            </FilterActionsGroup>
                        </FiltersCard>

                        {filteredTickets.length === 0 ? (
                            <EmptyCard>
                                <h2>No matches</h2>
                                <p>No tickets match your filters.</p>
                            </EmptyCard>
                        ) : (
                            <TicketListCard>
                                <DesktopMode>
                                    <TicketTableScroll>
                                        <TicketTable>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Priority</th>
                                                    <th>Question / Answer</th>
                                                    <th>Status</th>
                                                    <th>Assignee</th>
                                                    <th aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageTickets.map(ticket => {
                                                    const {
                                                        statusMeta,
                                                        priorityMeta,
                                                        assigneeLabel,
                                                    } = getTicketDisplayMeta(ticket)
                                                    return (
                                                        <tr
                                                            key={ticket.ticketId}
                                                            onClick={() =>
                                                                openTicket(
                                                                    ticket.ticketId,
                                                                )
                                                            }
                                                        >
                                                            <ColId>
                                                                {ticket.ticketId}
                                                            </ColId>
                                                            <td>
                                                                <PriorityPill
                                                                    $pill={
                                                                        priorityMeta.pill
                                                                    }
                                                                >
                                                                    {getPriorityDisplayLabel(
                                                                        ticket.priorityId,
                                                                    )}
                                                                </PriorityPill>
                                                            </td>
                                                            <td>
                                                                <QuestionCell>
                                                                    <div className="q">
                                                                        {ticket.questionText ||
                                                                            ticket.title ||
                                                                            '—'}
                                                                    </div>
                                                                    {ticket.answerText && (
                                                                        <span className="a">
                                                                            Answer:{' '}
                                                                            {
                                                                                ticket.answerText
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </QuestionCell>
                                                            </td>
                                                            <td>
                                                                <StatusBadge
                                                                    $pill={
                                                                        statusMeta.pill
                                                                    }
                                                                >
                                                                    {getStatusDisplayLabel(
                                                                        ticket.statusId,
                                                                    )}
                                                                </StatusBadge>
                                                                <WhenText>
                                                                    {getTicketStatusWhenLabel(
                                                                        ticket,
                                                                    )}
                                                                </WhenText>
                                                            </td>
                                                            <td>
                                                                <AssigneeCell>
                                                                    {assigneeLabel}
                                                                    {ticket.assigneeEmail &&
                                                                        ticket.assigneeFullName &&
                                                                        ticket.assigneeEmail
                                                                            .trim()
                                                                            .toLowerCase() !==
                                                                            ticket.assigneeFullName
                                                                                .trim()
                                                                                .toLowerCase() && (
                                                                            <span className="email">
                                                                                {
                                                                                    ticket.assigneeEmail
                                                                                }
                                                                            </span>
                                                                        )}
                                                                </AssigneeCell>
                                                            </td>
                                                            <RowAction>→</RowAction>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </TicketTable>
                                    </TicketTableScroll>
                                </DesktopMode>
                                <MobileMode>
                                    <TicketMobileList>
                                        {pageTickets.map(ticket =>
                                            renderTicketMobileCard(
                                                ticket,
                                                openTicket,
                                            ),
                                        )}
                                    </TicketMobileList>
                                </MobileMode>

                                <Pager>
                                    <span>
                                        Showing {pageStart}–{pageEnd} of{' '}
                                        {filteredTickets.length} ticket
                                        {filteredTickets.length === 1
                                            ? ''
                                            : 's'}
                                    </span>
                                    <div className="right">
                                        <button
                                            type="button"
                                            disabled={safePage <= 1}
                                            onClick={() =>
                                                setPage(p =>
                                                    Math.max(1, p - 1),
                                                )
                                            }
                                        >
                                            ← Prev
                                        </button>
                                        <span>
                                            Page <strong>{safePage}</strong> of{' '}
                                            {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={safePage >= totalPages}
                                            onClick={() =>
                                                setPage(p =>
                                                    Math.min(
                                                        totalPages,
                                                        p + 1,
                                                    ),
                                                )
                                            }
                                        >
                                            Next →
                                        </button>
                                        <select
                                            aria-label="Results per page"
                                            value={pageSize}
                                            onChange={e => {
                                                setPageSize(
                                                    Number(e.target.value),
                                                )
                                                setPage(1)
                                            }}
                                        >
                                            <option value={10}>
                                                10 per page
                                            </option>
                                            <option value={25}>
                                                25 per page
                                            </option>
                                            <option value={50}>
                                                50 per page
                                            </option>
                                        </select>
                                    </div>
                                </Pager>
                            </TicketListCard>
                        )}
                    </>
                )}

                {!error && tickets.length === 0 && (
                    <EmptyCard>
                        <h2>No tickets yet</h2>
                        <p>
                            When auto-ticket rules fire on completed audits,
                            tickets will appear here.
                        </p>
                    </EmptyCard>
                )}
            </ListPageInner>
        </PageWrap>
    )
}
